import React, { useEffect, useRef, useState } from 'react';
import { insuranceApi } from './api';
import { COVERAGE_OPTIONS } from './insuranceConfig';
import ModalShell from './ModalShell';

// Normaliza una fecha (ISO o YYYY-MM-DD) a YYYY-MM-DD para <input type="date">
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * Corrección tipográfica: edita los valores del MISMO seguro con todo
 * precargado. No reenvía correo ni reprocesa con IA. Adjuntar es opcional.
 */
export default function CorrectionForm({ visible, onHide, insurance, toast, onDone }) {
  const [policyNumber, setPolicyNumber] = useState('');
  const [coverage, setCoverage] = useState('Amplia');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [planYears, setPlanYears] = useState(1);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Precargar con los valores actuales del seguro al abrir
  useEffect(() => {
    if (!visible || !insurance) return;
    setPolicyNumber(insurance.policy_number || '');
    setCoverage(insurance.coverage_type || 'Amplia');
    setStartDate(toDateInput(insurance.start_date));
    setEndDate(toDateInput(insurance.end_date));
    setAmount(insurance.amount ?? '');
    setPlanYears(insurance.payment_plan_years || 1);
    setAttachments([]);
  }, [visible, insurance]);

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []).map((file) => ({
      id: 'c-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      file,
      nombre: file.name,
    }));
    setAttachments((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSave = !!policyNumber && !!startDate && !!endDate && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('policy_number', policyNumber);
      fd.append('coverage_type', coverage);
      fd.append('start_date', startDate);
      fd.append('end_date', endDate);
      if (amount !== '') fd.append('amount', amount);
      fd.append('payment_plan_years', planYears);
      attachments.forEach((a, i) => {
        fd.append(`attachments[${i}][file]`, a.file);
        fd.append(`attachments[${i}][label]`, 'corrección');
      });

      const res = await insuranceApi.correct(insurance.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Corrección aplicada', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo corregir', detail: e?.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={saving ? () => {} : onHide} stacked width={620}>
      <button className="modal-close" onClick={onHide} disabled={saving}><i className="pi pi-times" /></button>

      <div style={{ padding: '30px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Corrección tipográfica</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.01em', marginBottom: 6 }}>
          Editar datos de la póliza
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)' }}>
          Edita directamente los valores. No se reenvía correo ni se reprocesa con IA.
        </div>
      </div>

      <div className="scrollable" style={{ padding: '8px 32px 16px', flex: 1, minHeight: 0 }}>
        <div style={{ marginBottom: 14 }}>
          <div className="pei-label">Número de póliza</div>
          <input className="pei-input" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Cobertura</div>
            <select className="pei-input" value={coverage} onChange={(e) => setCoverage(e.target.value)}>
              {COVERAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="pei-label">Plan (años)</div>
            <input className="pei-input" type="number" min={1} max={10} value={planYears} onChange={(e) => setPlanYears(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Fecha inicio</div>
            <input className="pei-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Fecha fin</div>
            <input className="pei-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div className="pei-label">Precio anual (MXN)</div>
          <input className="pei-input" type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>

        {/* Adjuntos opcionales — solo se guardan, no se leen con IA */}
        <div className="pei-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <i className="pi pi-paperclip" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
          Adjuntos opcionales
          <span style={{ color: 'var(--pei-text-helper)', fontWeight: 500, letterSpacing: 0, textTransform: 'none', marginLeft: 4, fontSize: 11 }}>
            · no se procesan con IA
          </span>
        </div>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {attachments.map((f) => (
            <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 10px', background: 'white', border: '1px solid var(--pei-gray-5)', borderRadius: 999, fontSize: 12 }}>
              <i className="pi pi-file" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
              <span style={{ color: 'var(--pei-text)', fontWeight: 500 }}>{f.nombre}</span>
              <span onClick={() => setAttachments(attachments.filter((a) => a.id !== f.id))} style={{ cursor: 'pointer', color: 'var(--pei-text-helper)', padding: '0 4px', fontSize: 14 }}>×</span>
            </span>
          ))}
          <button className="chip-add" onClick={() => fileInputRef.current?.click()} type="button">
            <i className="pi pi-paperclip" style={{ fontSize: 11 }} /> Adjuntar
          </button>
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={saving} style={{ flex: '0 0 auto' }}>Cancelar</button>
        <button className="pei-btn pei-btn-primary floor-shadow" onClick={handleSave} disabled={!canSave} style={{ flex: 1 }}>
          {saving ? 'Guardando…' : 'Guardar corrección'} <i className="pi pi-check" style={{ fontSize: 12 }} />
        </button>
      </div>
    </ModalShell>
  );
}
